import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Cal, { getCalApi } from '@calcom/embed-react';
import { Send, CheckCircle, ChevronDown, ExternalLink } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { getPrimaryBaseUrl } from '../../config/apiConfig';
import {
  CAL_EMBED_LINK,
  CAL_EMBED_NAMESPACE,
  buildDemoBookingUrl,
} from '../../config/demoBooking';
import {
  buildMetaTrackingPayload,
  createMetaEventId,
  metaPixelTrackLead,
} from '../../utils/metaPixel';
import './DemoModal.css';

function extractCalBookingContact(detail: unknown): { email?: string; name?: string } {
  if (!detail || typeof detail !== 'object') {
    return {};
  }
  const root = detail as Record<string, unknown>;
  const data =
    root.data && typeof root.data === 'object'
      ? (root.data as Record<string, unknown>)
      : root;
  const attendees = Array.isArray(data.attendees) ? data.attendees : [];
  const firstAttendee =
    attendees[0] && typeof attendees[0] === 'object'
      ? (attendees[0] as Record<string, unknown>)
      : undefined;
  const email =
    (typeof data.email === 'string' && data.email) ||
    (typeof firstAttendee?.email === 'string' && firstAttendee.email) ||
    undefined;
  const name =
    (typeof data.name === 'string' && data.name) ||
    (typeof firstAttendee?.name === 'string' && firstAttendee.name) ||
    undefined;
  return { email, name };
}

interface DemoModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialEmail?: string;
  initialName?: string;
  /** Appelé après réservation Cal.com réussie (embed). */
  onBooked?: () => void;
}

export default function DemoModal({
  isOpen,
  onClose,
  initialEmail,
  initialName,
  onBooked,
}: DemoModalProps) {
  const { t } = useTranslation('marketing');
  const contactEmail = t('demoModal.contactEmail');
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const formRef = useRef(form);
  formRef.current = form;
  const prevOpen = useRef(false);
  const fallbackRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen && !prevOpen.current && (initialName != null || initialEmail != null)) {
      setForm((prev) => ({
        ...prev,
        name: initialName ?? prev.name,
        email: initialEmail ?? prev.email,
      }));
    }
    prevOpen.current = isOpen;
  }, [isOpen, initialName, initialEmail]);

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [fallbackOpen, setFallbackOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFallbackOpen(false);
    setStatus('idle');
  }, [isOpen]);

  useEffect(() => {
    if (status !== 'error') return;
    setFallbackOpen(true);
    requestAnimationFrame(() => {
      fallbackRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }, [status]);

  const onBookedRef = useRef(onBooked);
  onBookedRef.current = onBooked;

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      const cal = await getCalApi({ namespace: CAL_EMBED_NAMESPACE });
      if (cancelled) return;
      cal('ui', {
        theme: 'dark',
        hideEventTypeDetails: false,
        layout: 'month_view',
      });
      cal('on', {
        action: 'bookingSuccessful',
        callback: (event: { detail?: unknown }) => {
          const fromBooking = extractCalBookingContact(event?.detail);
          const email =
            fromBooking.email?.trim() ||
            formRef.current.email.trim() ||
            (typeof initialEmail === 'string' ? initialEmail.trim() : '');
          if (!email) {
            return;
          }
          const name =
            fromBooking.name?.trim() ||
            formRef.current.name.trim() ||
            (typeof initialName === 'string' ? initialName.trim() : undefined);
          metaPixelTrackLead({
            em: email,
            fn: name,
            content_name: 'Demo Cal Booking',
            content_category: 'Demo',
            event_id: createMetaEventId('cal-'),
          });

          // +7 j d'essai si le pro est déjà en trial Premium (idempotent côté API)
          const finish = () => {
            try {
              onBookedRef.current?.();
            } catch {
              /* ignore */
            }
          };

          const token =
            typeof localStorage !== 'undefined'
              ? localStorage.getItem('token')
              : null;
          if (!token) {
            finish();
            return;
          }

          axios
            .post(`${getPrimaryBaseUrl()}/extend-trial-demo-bonus`, { token })
            .then((resp) => {
              const msg = resp?.data?.message;
              if (resp?.data?.already_applied) {
                toast.info(
                  msg ||
                    t('demoModal.trialBonusAlreadyApplied', {
                      defaultValue: 'Bonus démo déjà appliqué sur ton essai.',
                    })
                );
              } else if (msg) {
                toast.success(msg);
              }
            })
            .catch((err: unknown) => {
              const apiMsg = (
                err as { response?: { data?: { error?: string; code?: string } } }
              )?.response?.data?.error;
              const code = (
                err as { response?: { data?: { code?: string } } }
              )?.response?.data?.code;
              if (code === 'NO_PREMIUM_TRIAL' || code === 'NO_STRIPE_CUSTOMER') {
                toast.info(
                  apiMsg ||
                    t('demoModal.trialBonusNoTrial', {
                      defaultValue:
                        'Démo réservée. Le +7 j s’applique seulement pendant un essai Premium.',
                    })
                );
              } else {
                toast.warning(
                  apiMsg ||
                    t('demoModal.trialBonusError', {
                      defaultValue:
                        'Démo réservée, mais la prolongation d’essai a échoué. Contacte le support.',
                    })
                );
              }
            })
            .finally(finish);
        },
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, initialEmail, initialName]);

  const [calInstance, setCalInstance] = useState(0);
  useEffect(() => {
    if (!isOpen) return;
    setCalInstance((n) => n + 1);
  }, [isOpen]);

  const emailForCal =
    form.email.trim() || (typeof initialEmail === 'string' ? initialEmail.trim() : '');
  const nameForCal =
    form.name.trim() || (typeof initialName === 'string' ? initialName.trim() : '');

  const calConfig = {
    layout: 'month_view' as const,
    useSlotsViewOnSmallScreen: 'true',
    theme: 'dark' as const,
    ...(emailForCal ? { email: emailForCal } : {}),
    ...(nameForCal ? { name: nameForCal } : {}),
  };

  const calOpenInNewTabUrl = buildDemoBookingUrl({
    email: emailForCal || undefined,
    name: nameForCal || undefined,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;

    setStatus('loading');
    const eventId = createMetaEventId('demo-');
    const metaTracking = buildMetaTrackingPayload(eventId);
    try {
      const API_URL = getPrimaryBaseUrl();
      metaPixelTrackLead({
        em: form.email,
        fn: form.name,
        content_name: 'Demo Request Fallback',
        content_category: 'Demo',
        event_id: eventId,
      });
      await axios.post(`${API_URL}/request-demo`, {
        companyName: form.name,
        email: form.email,
        message: form.message,
        meta_tracking: metaTracking,
      });

      setStatus('success');
      setTimeout(() => {
        onClose();
        setStatus('idle');
        setForm({ name: '', email: '', message: '' });
      }, 2200);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3200);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="demobook-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
        >
          <motion.div
            className="demobook-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="demobook-title"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="demobook-header">
              <div className="demobook-header-top">
                <h2 id="demobook-title">{t('demoModal.title')}</h2>
                <button type="button" className="demobook-close" onClick={onClose} aria-label={t('demoModal.closeAriaLabel')}>
                  <span className="demobook-close-x" aria-hidden="true" />
                </button>
              </div>
              <p className="demobook-tagline">
                {t('demoModal.tagline')}
              </p>
            </header>

            <div className="demobook-body">
              <div className="demobook-cal-section">
                <div className="demobook-cal-box">
                  <Cal
                    key={calInstance}
                    namespace={CAL_EMBED_NAMESPACE}
                    calLink={CAL_EMBED_LINK}
                    config={calConfig}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      minHeight: 'clamp(260px, 38dvh, 440px)',
                      display: 'block',
                      overflow: 'hidden',
                    }}
                  />
                </div>

                <div className="demobook-cal-footer">
                  <a
                    href={calOpenInNewTabUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="demobook-external"
                  >
                    <ExternalLink size={16} aria-hidden />
                    {t('demoModal.openCalendarInNewTab')}
                  </a>
                </div>

                <button
                  type="button"
                  className="demobook-fallback-toggle"
                  aria-expanded={fallbackOpen}
                  aria-controls="demobook-fallback"
                  onClick={() => setFallbackOpen((v) => !v)}
                >
                  <span>
                    {fallbackOpen
                      ? t('demoModal.fallbackToggleCollapse')
                      : t('demoModal.fallbackToggleExpand')}
                  </span>
                  <ChevronDown
                    size={20}
                    strokeWidth={2.25}
                    className={`demobook-fallback-chevron${fallbackOpen ? ' is-open' : ''}`}
                    aria-hidden
                  />
                </button>
              </div>

              {fallbackOpen && (
                <section
                  ref={fallbackRef}
                  id="demobook-fallback"
                  className="demobook-fallback"
                >
                  {status === 'success' && (
                    <p className="demobook-banner demobook-banner--ok">
                      <CheckCircle size={18} aria-hidden /> {t('demoModal.successBanner')}
                    </p>
                  )}
                  {status === 'error' && (
                    <p className="demobook-banner demobook-banner--err">
                      {t('demoModal.errorBeforeEmail')}{' '}
                      <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
                    </p>
                  )}

                  <p className="demobook-lead">
                    {t('demoModal.lead')}
                  </p>

                  <form className="demobook-form" onSubmit={handleSubmit}>
                    <label className="demobook-label" htmlFor="db-name">
                      {t('demoModal.nameLabel')}
                    </label>
                    <input
                      id="db-name"
                      name="name"
                      className="demobook-input"
                      value={form.name}
                      onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                      required
                      disabled={status === 'loading'}
                      autoComplete="name"
                    />

                    <label className="demobook-label" htmlFor="db-email">
                      {t('demoModal.emailLabel')}
                    </label>
                    <input
                      id="db-email"
                      name="email"
                      type="email"
                      className="demobook-input"
                      value={form.email}
                      onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                      required
                      disabled={status === 'loading'}
                      autoComplete="email"
                    />

                    <label className="demobook-label" htmlFor="db-msg">
                      {t('demoModal.messageLabel')}
                    </label>
                    <textarea
                      id="db-msg"
                      name="message"
                      className="demobook-textarea"
                      value={form.message}
                      onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                      rows={5}
                      disabled={status === 'loading'}
                    />

                    <button
                      type="submit"
                      className="demobook-submit"
                      disabled={status === 'loading' || !form.name.trim() || !form.email.trim()}
                    >
                      {status === 'loading' ? (
                        t('demoModal.submitting')
                      ) : (
                        <>
                          <Send size={18} aria-hidden /> {t('demoModal.submit')}
                        </>
                      )}
                    </button>
                  </form>
                </section>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
